/*
  WCAG 2.x relative luminance and contrast ratio, as the brief's Phase 0 contrast test
  and the theme validator (brief §2.8 "Validate on save") both need. The C# side
  (Core.Branding in the API solution) implements the same formulas; the server is authoritative on
  save and this copy gives the owner an answer before the round trip.
*/

export interface Rgb {
  r: number
  g: number
  b: number
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

/** `#rgb` or `#rrggbb` (hash optional) to 0..255 channels. Throws on anything else. */
export function parseHex(hex: string): Rgb {
  const match = HEX.exec(hex.trim())
  if (!match || !match[1]) {
    throw new Error(`Not a hex colour: "${hex}"`)
  }
  let digits = match[1]
  if (digits.length === 3) {
    digits = digits
      .split('')
      .map((d) => d + d)
      .join('')
  }
  return {
    r: parseInt(digits.slice(0, 2), 16),
    g: parseInt(digits.slice(2, 4), 16),
    b: parseInt(digits.slice(4, 6), 16),
  }
}

export function isHexColor(value: string): boolean {
  return HEX.test(value.trim())
}

export function toHex({ r, g, b }: Rgb): string {
  const channel = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0')
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase()
}

function linear(channel: number): number {
  const c = channel / 255
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(color: Rgb | string): number {
  const { r, g, b } = typeof color === 'string' ? parseHex(color) : color
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
}

/** WCAG contrast ratio, 1 to 21. Order of arguments does not matter. */
export function contrastRatio(a: Rgb | string, b: Rgb | string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const lighter = Math.max(la, lb)
  const darker = Math.min(la, lb)
  return (lighter + 0.05) / (darker + 0.05)
}

/** AA thresholds: normal text 4.5, large text and UI component boundaries 3. */
export const AA_TEXT = 4.5
export const AA_LARGE = 3

/** Two decimals, as the brief and the extensions file quote them ("5.47:1"). */
export function formatRatio(ratio: number): string {
  return `${ratio.toFixed(2)}:1`
}
